import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { GlobalEmittersService } from 'src/app/services/global-emitters.service';
import { DataService } from '../../../../services/data.service';
import { MeasurementService } from '../../../../services/measurement.service';
import { BoltDimensionModel, BoltMaterialModel, DiagramInfo, EngineeredJointInputData } from '../../models/';
import { Router } from '@angular/router';
import { pipeSpecVersion } from 'src/app/Modules/settings/models/idms/pipe-specification.model';
import { UnitType } from 'src/app/models/enums/unit-type.enum';
import { IdmsSharedService } from 'src/app/Modules/idms/services/idms-shared.service';
import { Subject, takeUntil } from 'rxjs';
import { TighteningMethod } from '../../enums/tightening-method.enum';

@Component({
    selector: 'app-engineered-joint-input',
    templateUrl: './engineered-joint-input.component.html',
    styleUrls: ['./engineered-joint-input.component.scss'],
    standalone: false
})
export class EngineeredJointInputComponent implements OnInit {
    @Input() isRedirect: boolean;
    @Input() pipeSpecVersion: pipeSpecVersion;
    @Output() returnData: EventEmitter<EngineeredJointInputData> = new EventEmitter();
    @Output() diagram: EventEmitter<DiagramInfo> = new EventEmitter();
    @Input() calculationType: any; // require when pipespec calc
    destroy$ = new Subject();

    tooltip_position = 'above';
    boltType: string;
    boltDimensionId: string;
    boltMaterialId: string;
    jointConfiguration: string;
    flangeMaterial1: string;
    flangeMaterial2: string;
    threadTypeDefault: string;
    boltQuantityDefault: string;
    minimumBoltDiameterDefault: string;
    nutStandardId: string;
    jointThicknessDefault: string;
    boltHoleDiameterDefault: string;
    gasketCompThicknessDefault: string;
    tighteningSpecificationDefault: string = 'BoltLoad';
    tighteningSpecOverideDefault: string;
    gasketId: string;
    boltMaterialReset: boolean = false;
    flangeMaterial1Reset: boolean = false;
    flangeMaterial2Reset: boolean = false;
    gasketReset: boolean = false;
    pAdditionalDefault: number;
    inputFormGroup: UntypedFormGroup;
    engineeredJointInputData: EngineeredJointInputData;
    tighteningMethod: TighteningMethod;
    boltMaterialAliasId:string='';
    flangeMaterialAliasId1:string='';
    flangeMaterialAliasId2:string='';
    gasketAliasId:string='';
    constructor(
        private fb: UntypedFormBuilder,
        private dataSvc: DataService,
        private globalEmittersService: GlobalEmittersService,
        private meaService: MeasurementService,
        public router: Router,
        public idmsSharedService: IdmsSharedService
    ) {}

    ngOnInit(): void {
        this.engineeredJointInputData = new EngineeredJointInputData();
        this.loadFormGroup();
        this.inputFormGroup = this.fb.group({
            jointConfiguration: [this.jointConfiguration, Validators.required],
            boltType: [this.boltType, Validators.required],
            threadType: [this.threadTypeDefault, Validators.required],
            boltSize: [this.boltDimensionId, Validators.required],
            boltQuantity: [this.boltQuantityDefault, [Validators.required, Validators.min(1)]],
            isRedAreaBoltSelected: [false],
            redAreaBolt: [this.minimumBoltDiameterDefault, { updateOn: 'blur' }],
            nutStandard: [this.nutStandardId, Validators.required],
            jointThickness: [
                this.jointThicknessDefault,
                {
                    validators: Validators.required,
                    asyncValidators: null,
                    updateOn: 'blur',
                },
            ],
            boltHoleDiameter: [
                this.boltHoleDiameterDefault,
                {
                    validators: Validators.required,
                    asyncValidators: null,
                    updateOn: 'blur',
                },
            ],
            gasketCompThickness: [
                this.gasketCompThicknessDefault,
                {
                    validators: Validators.required,
                    asyncValidators: null,
                    updateOn: 'blur',
                },
            ],
            tighteningSpecification: [this.tighteningSpecificationDefault, Validators.required],
            tighteningSpecOveride: [this.tighteningSpecOverideDefault, { validators: Validators.required, syncValidators: null, updateOn: 'blur' }],
            boltMaterial: [this.boltMaterialId, Validators.required],
            flangeMaterial1: [this.flangeMaterial1, Validators.required],
            flangeMaterial2: [this.flangeMaterial2, Validators.required],
            gasket: [this.gasketId, Validators.required],
            EngineeredJointDiagramId: [''],
            ispAdditional: [false],
            pAdditional: [this.pAdditionalDefault],
            boltMaterialAliasId: [''],
            flangeMaterialAliasId1: [''],
            flangeMaterialAliasId2: [''],
            gasketAliasId: [''],


        });

        this.inputFormGroup.markAllAsTouched();
        this.globalEmittersService.engineerJointIFormGroup.next(this.inputFormGroup);
        this.inputFormGroup
            .get('EngineeredJointDiagramId')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value) => this.emitDiagramEvent('EngineeredJoint', value, null));
        this.inputFormGroup
            .get('boltType')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value) => (this.boltType = value));

        this.inputFormGroup
            .get('boltSize')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value) => (this.boltDimensionId = value));
        if (this.idmsSharedService.enableConfirmWithRouter(2) == true) {
            // Subscribe to valueChanges and emit the form when it changes
            this.inputFormGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
                this.btnEngJointConfirmClicked();
            });
        }

        this.inputFormGroup.get('ispAdditional').valueChanges.subscribe((value) => {
            if (value) {
                this.inputFormGroup.get('pAdditional').setValidators(Validators.required);
                this.inputFormGroup.get('pAdditional').setValue(0);
            } else {
                this.inputFormGroup.get('pAdditional')?.clearValidators();
                this.inputFormGroup.get('pAdditional')?.updateValueAndValidity();
            }
        });

        this.globalEmittersService.unitUpdated.subscribe((resp) => {
            if (
                resp &&
                (this.router.url == '/informate/engineeredjoint/false' || this.router.url == '/informate/engineeredjoint/true') &&
                this.inputFormGroup.get('EngineeredJointDiagramId').value
            ) {
                this.emitDiagramEvent('EngineeredJoint', this.inputFormGroup.get('EngineeredJointDiagramId').value, null);
            }
        });

        this.globalEmittersService.tighteningProcedureForm.subscribe((form: UntypedFormGroup) => {
            if (form) {
                form.get('calcMethod').valueChanges.subscribe((methodType: string) => {
                    this.tighteningMethod = methodType.toLowerCase() == 'tension' ? TighteningMethod.Tension : TighteningMethod.Torque;
                    if (this.tighteningMethod == 0) {
                        this.inputFormGroup.get('ispAdditional').setValue(false);
                    }
                });
            }
        });
    }

    loadFormGroup() {
        if (this.isRedirect && this.dataSvc.calculationData != null) {
            this.jointConfiguration = this.dataSvc.calculationData.input.others.jointConfiguration;
            this.boltType = this.dataSvc.calculationData.input.others.boltType == 1 ? 'Imperial' : 'Metric';
            this.threadTypeDefault = this.dataSvc.calculationData.input.others.threadType;
            this.boltDimensionId = this.dataSvc.calculationData.input.recordId.boltDimensionId;
            this.boltQuantityDefault = this.dataSvc.calculationData.input.others.boltQuantity as unknown as string;
            this.minimumBoltDiameterDefault = this.meaService.format(
                this.dataSvc.calculationData.input.calculationOverrides.minimumBoltDiameter,
                UnitType.DefaultLengthUnit,
                3
            );
            this.nutStandardId = this.dataSvc.calculationData.input.recordId.nutDimensionId;
            this.jointThicknessDefault = this.meaService.format(
                this.dataSvc.calculationData.input.calculationOverrides.jointThickness,
                UnitType.DefaultLengthUnit,
                3
            );
            this.boltHoleDiameterDefault = this.meaService.format(
                this.dataSvc.calculationData.input.calculationOverrides.boltHoleDiameter,
                UnitType.DefaultLengthUnit,
                3
            );
            this.gasketCompThicknessDefault = this.meaService.format(
                this.dataSvc.calculationData.input.calculationOverrides.gasketCompThickness,
                UnitType.DefaultLengthUnit,
                3
            );

            this.tighteningSpecificationDefault = this.dataSvc.calculationData.input.others.tighteningSpecification;
            (this.tighteningSpecOverideDefault = this.dataSvc.calculationData.input.others.tighteningSpecificationInput), UnitType.DefaultLengthUnit;

            this.boltMaterialId = this.dataSvc.calculationData.input.recordId.boltMaterialId;
            this.flangeMaterial1 = this.dataSvc.calculationData.input.recordId.flangeMaterialId1;
            this.flangeMaterial2 = this.dataSvc.calculationData.input.recordId.flangeMaterialId2;
            this.gasketId = this.dataSvc.calculationData.input.recordId.gasketId;
            this.globalEmittersService.selectedGasketId.next(this.gasketId);
        }
        if (this.idmsSharedService.enableConfirmWithRouter(2) == true && this.pipeSpecVersion) {
            this.jointConfiguration = this.pipeSpecVersion.others.jointConfiguration;
            this.boltType = this.pipeSpecVersion.others.boltType == 1 ? 'Imperial' : 'Metric';
            this.threadTypeDefault = this.pipeSpecVersion.others.threadType;
            this.boltDimensionId = this.pipeSpecVersion.recordId.boltDimensionId;
            this.boltQuantityDefault = this.pipeSpecVersion.others.boltQuantity as unknown as string;
            this.minimumBoltDiameterDefault = this.pipeSpecVersion.calculationOverrides.minimumBoltDiameter;
            this.nutStandardId = this.pipeSpecVersion.recordId.nutDimensionId;
            this.jointThicknessDefault = this.pipeSpecVersion.calculationOverrides.jointThickness;
            this.boltHoleDiameterDefault = this.pipeSpecVersion.calculationOverrides.boltHoleDiameter;
            this.gasketCompThicknessDefault = this.pipeSpecVersion.calculationOverrides.gasketCompThickness;

            this.tighteningSpecificationDefault = this.pipeSpecVersion.others.tighteningSpecification;
            this.tighteningSpecOverideDefault = this.pipeSpecVersion.others.tighteningSpecificationInput;

            this.boltMaterialId = this.pipeSpecVersion.recordId.boltMaterialId;
            this.flangeMaterial1 = this.pipeSpecVersion.recordId.flangeMaterialId1;
            this.flangeMaterial2 = this.pipeSpecVersion.recordId.flangeMaterialId2;
            this.gasketId = this.pipeSpecVersion.recordId.gasketId;
        }
    }

    setPrestine(controlName: string): void {
        switch (controlName) {
            case 'jointConfiguration':
                this.inputFormGroup.get('boltType').reset('', { emitEvent: false });
                this.inputFormGroup.get('threadType').reset('', { emitEvent: false });
                this.inputFormGroup.get('boltSize').reset('', { emitEvent: false });
                this.inputFormGroup.get('nutStandard').reset('', { emitEvent: false });
                this.inputFormGroup.get('boltMaterial').reset('', { emitEvent: false });
                this.inputFormGroup.get('flangeMaterial1').reset('', { emitEvent: false });
                this.inputFormGroup.get('flangeMaterial2').reset('', { emitEvent: false });
                this.inputFormGroup.get('gasket').reset('', { emitEvent: false });
                this.boltMaterialReset = true;
                this.flangeMaterial1Reset = true;
                this.flangeMaterial2Reset = true;
                this.gasketReset = true;
                break;
            case 'threadType':
                this.inputFormGroup.get('boltSize').reset('', { emitEvent: false });
                this.inputFormGroup.get('nutStandard').reset('', { emitEvent: false });
                this.inputFormGroup.get('boltMaterial').reset('', { emitEvent: false });
                this.inputFormGroup.get('flangeMaterial1').reset('', { emitEvent: false });
                this.inputFormGroup.get('flangeMaterial2').reset('', { emitEvent: false });
                this.inputFormGroup.get('gasket').reset('', { emitEvent: false });
                this.boltMaterialReset = true;
                this.flangeMaterial1Reset = true;
                this.flangeMaterial2Reset = true;
                this.gasketReset = true;
                break;
            case 'nutStandard':
                this.inputFormGroup.get('boltMaterial').reset('', { emitEvent: false });
                this.inputFormGroup.get('flangeMaterial1').reset('', { emitEvent: false });
                this.inputFormGroup.get('flangeMaterial2').reset('', { emitEvent: false });
                this.inputFormGroup.get('gasket').reset('', { emitEvent: false });
                this.boltMaterialReset = true;
                this.flangeMaterial1Reset = true;
                this.flangeMaterial2Reset = true;
                this.gasketReset = true;
                break;
            case 'boltMaterial':
                // this.inputFormGroup.get('flangeMaterial1').reset('', { emitEvent: false })
                // this.inputFormGroup.get('flangeMaterial2').reset('', { emitEvent: false })
                this.inputFormGroup.get('gasket').reset('', { emitEvent: false });
                // this.boltMaterialReset = true
                // this.flangeMaterial1Reset = true
                // this.flangeMaterial2Reset = true
                this.gasketReset = true;
                break;
        }
    }

    btnEngJointConfirmClicked(): void {
        this.inputFormGroup.markAllAsTouched();
        if (this.inputFormGroup.valid) {
            this.engineeredJointInputData.jointConfiguration = this.inputFormGroup.get('jointConfiguration').value;
            this.engineeredJointInputData.boltType = this.inputFormGroup.get('boltType').value;
            this.engineeredJointInputData.flangeMaterialId1 = this.inputFormGroup.get('flangeMaterial1').value;
            this.engineeredJointInputData.flangeMaterialId2 = this.inputFormGroup.get('flangeMaterial2').value;
            this.engineeredJointInputData.gasketId = this.inputFormGroup.get('gasket').value;
            this.engineeredJointInputData.boltMaterialId = this.inputFormGroup.get('boltMaterial').value;

            this.engineeredJointInputData.threadType = this.inputFormGroup.get('threadType').value;
            this.engineeredJointInputData.boltDimensionId = this.inputFormGroup.get('boltSize').value;
            this.engineeredJointInputData.boltQuantity = this.inputFormGroup.get('boltQuantity').value;
            this.engineeredJointInputData.nutDimensionId = this.inputFormGroup.get('nutStandard').value;
            this.engineeredJointInputData.isRedAreaBoltSelected = this.inputFormGroup.get('isRedAreaBoltSelected').value;
            if (this.inputFormGroup.get('isRedAreaBoltSelected').value == true) {
                this.engineeredJointInputData.minimumBoltDiameter = this.inputFormGroup.get('redAreaBolt').value;
            }

            this.engineeredJointInputData.tighteningSpecification = this.inputFormGroup.get('tighteningSpecification').value;
            if (this.engineeredJointInputData.tighteningSpecification != 'Informate Recommended') {
                this.engineeredJointInputData.tighteningSpecificationInput = this.inputFormGroup.get('tighteningSpecOveride').value;
            }

            this.engineeredJointInputData.jointThickness = this.inputFormGroup.get('jointThickness').value;
            this.engineeredJointInputData.boltHoleDiameter = this.inputFormGroup.get('boltHoleDiameter').value;
            this.engineeredJointInputData.gasketCompThickness = this.inputFormGroup.get('gasketCompThickness').value;
            this.engineeredJointInputData.isDataValid = true;

            this.engineeredJointInputData.pAdditional = this.inputFormGroup.get('ispAdditional').value ? this.inputFormGroup.get('pAdditional').value : null;

            this.engineeredJointInputData.boltMaterialAliasId = this.inputFormGroup.get('boltMaterialAliasId').value;
            this.engineeredJointInputData.flangeMaterialAliasId1 = this.inputFormGroup.get('flangeMaterialAliasId1').value;
            this.engineeredJointInputData.flangeMaterialAliasId2 = this.inputFormGroup.get('flangeMaterialAliasId2').value;
            this.engineeredJointInputData.gasketAliasId = this.inputFormGroup.get('gasketAliasId').value;
            this.returnData.emit(this.engineeredJointInputData);
        } else {
            this.engineeredJointInputData.isDataValid = false;
        }
    }

    onSelectedBoltType(selected: string) {
        this.boltType = selected;
        this.setPrestine('boltType');
    }

    onSelectedBoltSize(selected: BoltDimensionModel) {
        this.boltDimensionId = selected.boltDimensionId;
        this.setPrestine('boltDimension');
    }

    onSelectedBoltMaterial(selected: BoltMaterialModel) {
        this.boltMaterialId = selected.boltMaterialId;
        this.boltMaterialReset = false;
        this.setPrestine('boltMaterial');
    }

    onSelectedFlangeMaterial(controlId: string, selected: string) {
        this[controlId] = selected;
        this[`${controlId}Reset`] = false;
        this.setPrestine(controlId);
    }

    emitDiagramEvent(diagramType: string, diagramId: string, noOfBolts: number = 0) {
        let diagramInfo = new DiagramInfo();
        diagramInfo.diagramId = diagramId;
        diagramInfo.diagramType = diagramType;
        this.diagram.emit(diagramInfo);
    }
    get angleInput(): AbstractControl {
        return this.inputFormGroup.get('redAreaBolt');
    }
}
